import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  BadgeCheck,
  Bed,
  Building2,
  CheckCircle2,
  Coins,
  FileCheck,
  Loader2,
  Plus,
  RefreshCw,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ApiClientError } from '../../services/apiClient';
import {
  criarValidacaoMensal,
  downloadCertificadoPdf,
  criarLeito,
  emitirCertificado,
  getCertificado,
  getSaldoCredito,
  getCompatibilidadePerfil,
  listCertificados,
  listClientes,
  listEmpresasPorPapel,
  listLeitos,
  listarPerfisAssistenciais,
  listValidacoes,
  salvarCadeiaMinima,
  solicitarAtivacao,
} from '../../services/pilotoApi';
import {
  CertificadoPiloto,
  EmpresaPiloto,
  LeitoPiloto,
  SaldoCreditoPiloto,
  ValidacaoPiloto,
  PerfilAssistencialPiloto,
  CompatibilidadePerfilPiloto,
} from '../../types/piloto';

type CadeiaMinima = {
  origemEmpresaId: string;
  transporteEmpresaId: string;
  destinoEmpresaId: string;
};

const statusLabels: Record<string, string> = {
  pendente_ativacao: 'Pendente de ativação',
  em_validacao_inicial: 'Em validação inicial',
  ativo: 'Ativo',
  ativo_com_ressalva: 'Ativo com ressalva',
  nao_conforme: 'Não conforme',
  suspenso: 'Suspenso',
  desativado: 'Desativado',
  cancelado: 'Cancelado',
  pendente: 'Pendente',
  em_andamento: 'Em andamento',
  conforme: 'Conforme',
  conforme_com_ressalva: 'Conforme com ressalva',
  bloqueada: 'Bloqueada',
  emitido: 'Emitido',
  emitido_com_ressalva: 'Emitido com ressalva',
  nao_emitido: 'Não emitido',
};

const blockingValidationStatuses = ['bloqueada', 'nao_conforme', 'cancelada'];

function getCurrentCompetencia(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function formatError(error: unknown): string {
  if (error instanceof ApiClientError) {
    return `${error.code}: ${error.message}`;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Não foi possível concluir a operação.';
}

function hasPapel(empresa: EmpresaPiloto, papel: string): boolean {
  return empresa.papeis.some((item) => item.papel === papel && item.status === 'ativo');
}

const ActionButton: React.FC<{
  children: React.ReactNode;
  icon?: React.ElementType;
  disabled?: boolean;
  onClick: () => void;
}> = ({ children, disabled, icon: Icon, onClick }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className="inline-flex items-center justify-center gap-2 rounded-md bg-emerald-700 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:bg-gray-300"
  >
    {Icon ? <Icon size={16} /> : null}
    {children}
  </button>
);

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
    {children}
  </label>
);

const FluxoPiloto: React.FC = () => {
  const navigate = useNavigate();
  const { accessToken, hasPermission } = useAuth();
  const [clientes, setClientes] = useState<EmpresaPiloto[]>([]);
  const [origens, setOrigens] = useState<EmpresaPiloto[]>([]);
  const [transportes, setTransportes] = useState<EmpresaPiloto[]>([]);
  const [destinos, setDestinos] = useState<EmpresaPiloto[]>([]);
  const [clienteEmpresaId, setClienteEmpresaId] = useState('');
  const [saldo, setSaldo] = useState<SaldoCreditoPiloto | null>(null);
  const [leitos, setLeitos] = useState<LeitoPiloto[]>([]);
  const [leitoId, setLeitoId] = useState('');
  const [validacoes, setValidacoes] = useState<ValidacaoPiloto[]>([]);
  const [certificados, setCertificados] = useState<CertificadoPiloto[]>([]);
  const [certificadoDetalhe, setCertificadoDetalhe] = useState<CertificadoPiloto | null>(null);
  const [perfisAssistenciais, setPerfisAssistenciais] = useState<PerfilAssistencialPiloto[]>([]);
  const [perfilAssistencial, setPerfilAssistencial] = useState('curativo_complexo');
  const [compatibilidadePerfil, setCompatibilidadePerfil] = useState<CompatibilidadePerfilPiloto | null>(null);
  const [codigoLeito, setCodigoLeito] = useState(`LEITO-${Date.now().toString().slice(-5)}`);
  const [identificadorInterno, setIdentificadorInterno] = useState('');
  const [municipio, setMunicipio] = useState('Sao Paulo');
  const [uf, setUf] = useState('SP');
  const [regiao, setRegiao] = useState('Local');
  const [competencia, setCompetencia] = useState(getCurrentCompetencia());
  const [cadeia, setCadeia] = useState<CadeiaMinima>({
    origemEmpresaId: '',
    transporteEmpresaId: '',
    destinoEmpresaId: '',
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const selectedCliente = useMemo(
    () => clientes.find((cliente) => cliente.id === clienteEmpresaId) ?? null,
    [clientes, clienteEmpresaId]
  );

  const selectedLeito = useMemo(
    () => leitos.find((leito) => leito.id === leitoId) ?? null,
    [leitos, leitoId]
  );

  const selectedPerfil = useMemo(
    () => perfisAssistenciais.find((perfil) => perfil.id === perfilAssistencial) ?? null,
    [perfisAssistenciais, perfilAssistencial]
  );

  const validacaoAtual = useMemo(
    () =>
      validacoes.find((validacao) =>
        ['pendente', 'em_andamento'].includes(validacao.status)
      ) ?? validacoes[0] ?? null,
    [validacoes]
  );

  const canManageLeito = hasPermission('leitos.cadastrar');
  const canRequestActivation = hasPermission('leitos.solicitar_ativacao');
  const canEmitCertificate = hasPermission('certificados.emitir');

  const runAction = useCallback(async (action: () => Promise<void>, success: string) => {
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      await action();
      setMessage(success);
    } catch (actionError: unknown) {
      setError(formatError(actionError));
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshClienteData = useCallback(async () => {
    if (!accessToken || !clienteEmpresaId) {
      return;
    }

    const [saldoResult, leitosResult, certificadosResult] = await Promise.all([
      getSaldoCredito(accessToken, clienteEmpresaId),
      listLeitos(accessToken, clienteEmpresaId),
      listCertificados(accessToken, clienteEmpresaId),
    ]);

    setSaldo(saldoResult);
    setLeitos(leitosResult.items);
    setCertificados(certificadosResult.items);

    const nextLeitoId = leitoId || leitosResult.items[0]?.id || '';
    setLeitoId(nextLeitoId);
  }, [accessToken, clienteEmpresaId, leitoId]);

  const refreshValidacoes = useCallback(async () => {
    if (!accessToken || !leitoId) {
      setValidacoes([]);
      return;
    }

    const result = await listValidacoes(accessToken, leitoId);
    setValidacoes(result.items);
  }, [accessToken, leitoId]);

  const refreshCompatibilidade = useCallback(async () => {
    if (!accessToken || !leitoId) {
      setCompatibilidadePerfil(null);
      return;
    }
    setCompatibilidadePerfil(
      await getCompatibilidadePerfil(accessToken, leitoId, competencia)
    );
  }, [accessToken, leitoId, competencia]);

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    let active = true;
    setLoading(true);
    setError(null);

    Promise.all([
      listClientes(accessToken),
      listEmpresasPorPapel(accessToken, 'gerador'),
      listEmpresasPorPapel(accessToken, 'transportador'),
      listEmpresasPorPapel(accessToken, 'destinador'),
      listarPerfisAssistenciais(accessToken),
    ])
      .then(([clientesResult, origensResult, transportesResult, destinosResult, perfisResult]) => {
        if (!active) {
          return;
        }

        setClientes(clientesResult.items);
        setOrigens(origensResult.items.filter((empresa) => hasPapel(empresa, 'gerador')));
        setTransportes(
          transportesResult.items.filter((empresa) => hasPapel(empresa, 'transportador'))
        );
        setDestinos(destinosResult.items.filter((empresa) => hasPapel(empresa, 'destinador')));
        setPerfisAssistenciais(perfisResult.items);
        setPerfilAssistencial(perfisResult.items[0]?.id ?? 'outro');
        setClienteEmpresaId(clientesResult.items[0]?.id ?? '');
      })
      .catch((loadError: unknown) => {
        if (active) {
          setError(formatError(loadError));
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [accessToken]);

  useEffect(() => {
    runAction(refreshClienteData, 'Dados do cliente atualizados.');
  }, [clienteEmpresaId, refreshClienteData, runAction]);

  useEffect(() => {
    runAction(refreshValidacoes, 'Validações atualizadas.');
  }, [leitoId, refreshValidacoes, runAction]);

  useEffect(() => {
    refreshCompatibilidade().catch((loadError: unknown) => setError(formatError(loadError)));
  }, [refreshCompatibilidade]);

  const handleCriarLeito = () => {
    if (!accessToken || !clienteEmpresaId) {
      return;
    }

    runAction(async () => {
      const leito = await criarLeito(accessToken, {
        clienteEmpresaId,
        codigoLeito,
        identificadorInternoCliente: identificadorInterno || undefined,
        municipio,
        uf,
        regiao,
        perfilAssistencial,
      });
      setLeitoId(leito.id);
      setCodigoLeito(`LEITO-${Date.now().toString().slice(-5)}`);
      await refreshClienteData();
    }, 'Leito cadastrado para o cliente empresa.');
  };

  const handleSalvarCadeia = () => {
    if (
      !accessToken ||
      !leitoId ||
      !cadeia.origemEmpresaId ||
      !cadeia.transporteEmpresaId ||
      !cadeia.destinoEmpresaId
    ) {
      setError('Selecione leito, origem, transporte e destino para gravar a cadeia.');
      return;
    }

    runAction(async () => {
      await salvarCadeiaMinima(accessToken, leitoId, [
        { empresaId: cadeia.origemEmpresaId, papel: 'gerador', ordem: 1 },
        { empresaId: cadeia.transporteEmpresaId, papel: 'transportador', ordem: 2 },
        { empresaId: cadeia.destinoEmpresaId, papel: 'destinador', ordem: 3 },
      ]);
      await refreshClienteData();
      await refreshCompatibilidade();
    }, 'Cadeia mínima gravada no leito.');
  };

  const handleSolicitarAtivacao = () => {
    if (!accessToken || !leitoId) {
      return;
    }

    runAction(async () => {
      await solicitarAtivacao(accessToken, leitoId);
      await refreshClienteData();
      await refreshValidacoes();
    }, 'Solicitação enviada: crédito reservado e validação inicial aberta.');
  };

  const handleConcluirConforme = () => {
    if (!validacaoAtual) {
      return;
    }
    navigate(`/backoffice/workflow?validacaoId=${encodeURIComponent(validacaoAtual.id)}`);
  };

  const handleEmitirCertificado = () => {
    if (!accessToken || !leitoId) {
      return;
    }

    runAction(async () => {
      const validacoesResult = await listValidacoes(accessToken, leitoId);
      const validacaoBloqueante = validacoesResult.items.find(
        (validacao) =>
          validacao.tipo === 'mensal' &&
          validacao.competencia === competencia &&
          blockingValidationStatuses.includes(validacao.status)
      );

      if (validacaoBloqueante) {
        throw new ApiClientError(
          'CERTIFICADO_VALIDACAO_BLOQUEADA',
          `A competência ${competencia} possui validação mensal ${
            statusLabels[validacaoBloqueante.status] ?? validacaoBloqueante.status
          }. Resolva as pendências da cadeia antes de emitir o certificado.`
        );
      }

      let validacaoMensal =
        validacoesResult.items.find(
          (validacao) =>
            validacao.tipo === 'mensal' &&
            validacao.competencia === competencia &&
            ['conforme', 'conforme_com_ressalva'].includes(validacao.status)
        ) ??
        validacoesResult.items.find(
          (validacao) =>
            validacao.tipo === 'mensal' &&
            validacao.competencia === competencia &&
            ['pendente', 'em_andamento'].includes(validacao.status)
        );

      if (!validacaoMensal) {
        validacaoMensal = await criarValidacaoMensal(accessToken, leitoId, competencia);
      }

      if (['pendente', 'em_andamento'].includes(validacaoMensal.status)) {
        throw new ApiClientError(
          'CERTIFICADO_VALIDACAO_PENDENTE',
          'A validação mensal possui itens pendentes. Registre documentos e evidências reais antes de emitir o certificado.'
        );
      }

      const certificado = await emitirCertificado(accessToken, leitoId, competencia);
      const detalhe = await getCertificado(accessToken, certificado.id);
      setCertificadoDetalhe(detalhe);
      await refreshClienteData();
      await refreshValidacoes();
    }, 'Validação mensal concluída, certificado emitido e snapshot carregado.');
  };

  const handleVisualizarCertificado = (certificadoId: string) => {
    if (!accessToken) {
      return;
    }

    runAction(async () => {
      const detalhe = await getCertificado(accessToken, certificadoId);
      setCertificadoDetalhe(detalhe);
    }, 'Snapshot do certificado carregado.');
  };

  const handleDownloadCertificado = () => {
    if (!accessToken || !certificadoDetalhe) {
      return;
    }

    runAction(async () => {
      await downloadCertificadoPdf(accessToken, certificadoDetalhe.id);
    }, 'Download do certificado iniciado.');
  };

  if (!accessToken) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        Faça login pela API local para operar o fluxo do piloto.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 border-b border-gray-200 pb-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Fluxo do piloto</h2>
          <p className="mt-1 text-sm text-gray-600">
            Operação ponta a ponta com tenant operacional e cliente empresa.
          </p>
        </div>
        <ActionButton icon={RefreshCw} disabled={loading || !clienteEmpresaId} onClick={() => runAction(refreshClienteData, 'Dados atualizados.')}>
          Atualizar
        </ActionButton>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-white p-3 text-sm text-gray-600">
          <Loader2 className="animate-spin" size={16} />
          Processando operação...
        </div>
      ) : null}

      {message ? (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
          {message}
        </div>
      ) : null}

      {error ? (
        <div className="flex gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
          <AlertTriangle size={18} />
          <span>{error}</span>
        </div>
      ) : null}

      <section className="grid gap-4 lg:grid-cols-4">
        <div className="rounded-md border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <Building2 size={18} />
            Cliente empresa
          </div>
          <select
            value={clienteEmpresaId}
            onChange={(event) => setClienteEmpresaId(event.target.value)}
            className="mt-3 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">
              {clientes.length === 0 ? 'Nenhum cliente cadastrado' : 'Selecione'}
            </option>
            {clientes.map((cliente) => (
              <option key={cliente.id} value={cliente.id}>
                {cliente.razaoSocial}
              </option>
            ))}
          </select>
          <p className="mt-2 text-xs text-gray-500">
            {selectedCliente
              ? `${selectedCliente.tipoEmpresa ?? 'empresa participante'} dentro do tenant.`
              : 'Cadastre e aprove uma empresa cliente para iniciar o fluxo.'}
          </p>
          <div className="mt-3">
            <ActionButton icon={Building2} disabled={loading || !hasPermission('empresas.gerir')} onClick={() => navigate('/backoffice/gestao-empresas')}>
              Gerenciar empresas
            </ActionButton>
          </div>
        </div>

        <div className="rounded-md border border-gray-200 bg-white p-4">
          <div className="text-sm font-semibold text-gray-700">Saldo</div>
          <div className="mt-3 text-3xl font-semibold text-emerald-700">
            {saldo?.saldoDisponivel ?? 0}
          </div>
          <p className="text-xs text-gray-500">
            Comprados {saldo?.totalComprado ?? 0}, reservados {saldo?.totalReservado ?? 0},
            consumidos {saldo?.totalConsumido ?? 0}
          </p>
          <ActionButton icon={Coins} disabled={loading || !hasPermission('creditos.ajustar')} onClick={() => navigate('/Checkout/creditos')}>
            Gerenciar créditos
          </ActionButton>
        </div>

        <div className="rounded-md border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <Bed size={18} />
            Leitos
          </div>
          <div className="mt-3 text-3xl font-semibold text-gray-900">{leitos.length}</div>
          <p className="text-xs text-gray-500">
            Selecionado: {selectedLeito ? statusLabels[selectedLeito.status] ?? selectedLeito.status : 'nenhum'}
          </p>
        </div>

        <div className="rounded-md border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <FileCheck size={18} />
            Certificados
          </div>
          <div className="mt-3 text-3xl font-semibold text-gray-900">{certificados.length}</div>
          <p className="text-xs text-gray-500">Competência operacional {competencia}</p>
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-2">
        <div className="space-y-4">
          <div className="rounded-md border border-gray-200 bg-white p-4">
            <h3 className="text-lg font-semibold text-gray-900">1. Cadastro do leito</h3>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <div>
                <FieldLabel>Código do leito</FieldLabel>
                <input value={codigoLeito} onChange={(event) => setCodigoLeito(event.target.value)} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
              </div>
              <div>
                <FieldLabel>Identificador interno</FieldLabel>
                <input value={identificadorInterno} onChange={(event) => setIdentificadorInterno(event.target.value)} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
              </div>
              <div>
                <FieldLabel>Município</FieldLabel>
                <input value={municipio} onChange={(event) => setMunicipio(event.target.value)} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <FieldLabel>UF</FieldLabel>
                  <input maxLength={2} value={uf} onChange={(event) => setUf(event.target.value.toUpperCase())} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
                </div>
                <div>
                  <FieldLabel>Região</FieldLabel>
                  <input value={regiao} onChange={(event) => setRegiao(event.target.value)} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="md:col-span-2">
                <FieldLabel>Perfil assistencial operacional</FieldLabel>
                <select
                  value={perfilAssistencial}
                  onChange={(event) => setPerfilAssistencial(event.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  {perfisAssistenciais.map((perfil) => (
                    <option key={perfil.id} value={perfil.id}>{perfil.nome}</option>
                  ))}
                </select>
                {selectedPerfil ? (
                  <div className="mt-2 rounded-md border border-blue-100 bg-blue-50 p-3 text-xs text-blue-950">
                    <p>{selectedPerfil.descricao}</p>
                    <p className="mt-1"><strong>Resíduos esperados:</strong> {selectedPerfil.residuosEsperados.join(', ') || 'Definidos pelas flags operacionais.'}</p>
                    <p className="mt-1"><strong>Cadeia exigida:</strong> {selectedPerfil.cadeiaExigida.join(' → ')}</p>
                    <p className="mt-1"><strong>Grupos prováveis:</strong> {[
                      selectedPerfil.flagsPadrao.geraResiduoGrupoA ? 'A' : '',
                      selectedPerfil.flagsPadrao.geraResiduoGrupoBMedicamento ? 'B' : '',
                      selectedPerfil.flagsPadrao.geraResiduoGrupoEPerfurocortante ? 'E' : '',
                    ].filter(Boolean).join(', ') || 'Conforme classificação manual'}</p>
                  </div>
                ) : null}
              </div>
            </div>
            <div className="mt-4">
              <ActionButton icon={Plus} disabled={loading || !canManageLeito || !clienteEmpresaId} onClick={handleCriarLeito}>
                Criar leito
              </ActionButton>
            </div>
          </div>

          <div className="rounded-md border border-gray-200 bg-white p-4">
            <h3 className="text-lg font-semibold text-gray-900">2. Cadeia e ativação</h3>
            <div className="mt-4">
              <FieldLabel>Leito</FieldLabel>
              <select value={leitoId} onChange={(event) => setLeitoId(event.target.value)} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm">
                <option value="">Selecione</option>
                {leitos.map((leito) => (
                  <option key={leito.id} value={leito.id}>
                    {leito.codigoLeito} - {statusLabels[leito.status] ?? leito.status}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-3">
              <div>
                <FieldLabel>Origem</FieldLabel>
                <select value={cadeia.origemEmpresaId} onChange={(event) => setCadeia((prev) => ({ ...prev, origemEmpresaId: event.target.value }))} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm">
                  <option value="">Gerador</option>
                  {origens.map((empresa) => <option key={empresa.id} value={empresa.id}>{empresa.razaoSocial}</option>)}
                </select>
              </div>
              <div>
                <FieldLabel>Transporte</FieldLabel>
                <select value={cadeia.transporteEmpresaId} onChange={(event) => setCadeia((prev) => ({ ...prev, transporteEmpresaId: event.target.value }))} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm">
                  <option value="">Transportador</option>
                  {transportes.map((empresa) => <option key={empresa.id} value={empresa.id}>{empresa.razaoSocial}</option>)}
                </select>
              </div>
              <div>
                <FieldLabel>Destino</FieldLabel>
                <select value={cadeia.destinoEmpresaId} onChange={(event) => setCadeia((prev) => ({ ...prev, destinoEmpresaId: event.target.value }))} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm">
                  <option value="">Destinador</option>
                  {destinos.map((empresa) => <option key={empresa.id} value={empresa.id}>{empresa.razaoSocial}</option>)}
                </select>
              </div>
            </div>

            {compatibilidadePerfil ? (
              <div className={`mt-4 rounded-md border p-3 text-sm ${
                compatibilidadePerfil.compativel
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-950'
                  : 'border-amber-200 bg-amber-50 text-amber-950'
              }`}>
                <div className="flex items-center gap-2 font-semibold">
                  {compatibilidadePerfil.compativel ? <BadgeCheck size={17} /> : <AlertTriangle size={17} />}
                  {compatibilidadePerfil.compativel
                    ? 'Cadeia compatível com o perfil e os resíduos da competência.'
                    : 'Cadeia requer correção ou ressalva operacional.'}
                </div>
                <p className="mt-1 text-xs">
                  Perfil: {compatibilidadePerfil.perfilNome ?? 'não classificado'} · Grupos considerados: {compatibilidadePerfil.gruposConsiderados.join(', ') || 'nenhum'}
                </p>
                {compatibilidadePerfil.motivos.length > 0 ? (
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-xs">
                    {compatibilidadePerfil.motivos.map((motivo) => <li key={motivo}>{motivo}</li>)}
                  </ul>
                ) : null}
              </div>
            ) : null}

            <div className="mt-4 flex flex-wrap gap-2">
              <ActionButton icon={Building2} disabled={loading || !hasPermission('empresas.gerir')} onClick={() => navigate('/backoffice/gestao-empresas')}>
                Gerenciar fornecedores
              </ActionButton>
              <ActionButton icon={BadgeCheck} disabled={loading || !canManageLeito || !leitoId} onClick={handleSalvarCadeia}>
                Gravar cadeia
              </ActionButton>
              <ActionButton icon={CheckCircle2} disabled={loading || !canRequestActivation || !leitoId} onClick={handleSolicitarAtivacao}>
                Solicitar ativação
              </ActionButton>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-md border border-gray-200 bg-white p-4">
            <h3 className="text-lg font-semibold text-gray-900">3. Validação inicial</h3>
            {validacaoAtual ? (
              <div className="mt-3 space-y-3 text-sm">
                <div className="flex items-center justify-between rounded-md bg-gray-50 p-3">
                  <span>{validacaoAtual.tipo} - {validacaoAtual.competencia ?? 'sem competência'}</span>
                  <span className="font-medium text-gray-800">{statusLabels[validacaoAtual.status] ?? validacaoAtual.status}</span>
                </div>
                <p className="text-gray-600">
                  Itens da cadeia: {validacaoAtual.itens?.length ?? 0}. A análise deve ser feita na central com documentos e evidências reais.
                </p>
                {validacaoAtual.itens?.length ? (
                  <div className="space-y-2">
                    {validacaoAtual.itens.map((item) => (
                      <div key={item.id} className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2">
                        <span>{item.papel}</span>
                        <span className="font-medium">{statusLabels[item.status] ?? item.status}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  <ActionButton icon={CheckCircle2} disabled={loading} onClick={handleConcluirConforme}>
                    Abrir na central de validação
                  </ActionButton>
                </div>
              </div>
            ) : (
              <p className="mt-3 text-sm text-gray-500">Nenhuma validação aberta para o leito selecionado.</p>
            )}
          </div>

          <div className="rounded-md border border-gray-200 bg-white p-4">
            <h3 className="text-lg font-semibold text-gray-900">4. Certificado mensal</h3>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
              <div>
                <FieldLabel>Competência</FieldLabel>
                <input type="month" value={competencia} onChange={(event) => setCompetencia(event.target.value)} className="mt-1 rounded-md border border-gray-300 px-3 py-2 text-sm" />
              </div>
              <ActionButton icon={FileCheck} disabled={loading || !canEmitCertificate || !leitoId} onClick={handleEmitirCertificado}>
                Emitir certificado
              </ActionButton>
            </div>

            <div className="mt-4 space-y-2">
              {certificados.length === 0 ? (
                <p className="text-sm text-gray-500">Nenhum certificado para o cliente selecionado.</p>
              ) : certificados.map((certificado) => (
                <button
                  key={certificado.id}
                  type="button"
                  onClick={() => handleVisualizarCertificado(certificado.id)}
                  className="flex w-full items-center justify-between rounded-md border border-gray-200 px-3 py-2 text-left text-sm hover:bg-gray-50"
                >
                  <span>{certificado.competencia}</span>
                  <span>{statusLabels[certificado.status] ?? certificado.status}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {certificadoDetalhe?.snapshotHtml ? (
        <section className="rounded-md border border-gray-200 bg-white p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Snapshot do certificado</h3>
              <p className="mt-1 text-xs text-gray-500">
                Hash: {certificadoDetalhe.hashSnapshot}
              </p>
            </div>
            <ActionButton
              icon={FileCheck}
              disabled={loading || !['emitido', 'emitido_com_ressalva'].includes(certificadoDetalhe.status)}
              onClick={handleDownloadCertificado}
            >
              Baixar PDF
            </ActionButton>
          </div>
          <iframe
            title="Snapshot do certificado"
            srcDoc={certificadoDetalhe.snapshotHtml}
            className="mt-4 h-[520px] w-full rounded-md border border-gray-200 bg-white"
          />
        </section>
      ) : null}
    </div>
  );
};

export default FluxoPiloto;
